import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  ChefHat
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScreenshotProtection } from '../components/ScreenshotProtection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Sabzi', 'Daal', 'Salad', 'SpecialMain', 'SpecialRoti', 
  'SpecialRaita', 'SpecialSide', 'Sweet', 'Other'
];

// Sort order for categories
const CATEGORY_ORDER = {
  'Sabzi': 1,
  'Daal': 2,
  'Salad': 3,
  'SpecialMain': 4,
  'SpecialRoti': 5,
  'SpecialRaita': 6,
  'SpecialSide': 7,
  'Sweet': 8,
  'Other': 9
};

export const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      toast.error('Failed to fetch recipes');
    } finally {
      setLoading(false);
    }
  };

  // Sort and filter recipes
  const sortedAndFilteredRecipes = recipes
    .filter(recipe => {
      const matchesSearch = recipe.recipe_name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || recipe.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // First sort by category order
      const categoryDiff = (CATEGORY_ORDER[a.category] || 99) - (CATEGORY_ORDER[b.category] || 99);
      if (categoryDiff !== 0) return categoryDiff;
      // Then alphabetically
      return a.recipe_name.localeCompare(b.recipe_name);
    });

  // Group by category
  const groupedRecipes = sortedAndFilteredRecipes.reduce((groups, recipe) => {
    const cat = recipe.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(recipe);
    return groups;
  }, {});

  const getCategoryColor = (category) => {
    const colors = {
      Daal: 'bg-orange-500',
      Sabzi: 'bg-green-500',
      Salad: 'bg-cyan-500',
      SpecialMain: 'bg-[#faa023]',
      SpecialRoti: 'bg-[#faa023]',
      SpecialRaita: 'bg-[#faa023]',
      SpecialSide: 'bg-[#faa023]',
      Sweet: 'bg-pink-500',
      Other: 'bg-stone-400'
    };
    return colors[category] || colors.Other;
  };

  const getCategoryBgColor = (category) => {
    const colors = {
      Daal: 'border-l-orange-500 hover:bg-orange-50',
      Sabzi: 'border-l-green-500 hover:bg-green-50',
      Salad: 'border-l-cyan-500 hover:bg-cyan-50',
      SpecialMain: 'border-l-[#faa023] hover:bg-[#faa023]/10',
      SpecialRoti: 'border-l-[#faa023] hover:bg-[#faa023]/10',
      SpecialRaita: 'border-l-[#faa023] hover:bg-[#faa023]/10',
      SpecialSide: 'border-l-[#faa023] hover:bg-[#faa023]/10',
      Sweet: 'border-l-pink-500 hover:bg-pink-50',
      Other: 'border-l-stone-400 hover:bg-stone-50'
    };
    return colors[category] || colors.Other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <ScreenshotProtection>
    <div className="space-y-6" data-testid="recipes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight">
            Recipes
          </h1>
          <p className="text-stone-500 mt-1">{recipes.length} recipes in total</p>
        </div>
        <button
          onClick={() => navigate('/recipes/new')}
          className="btn-primary"
          data-testid="add-recipe-btn"
        >
          <Plus size={20} />
          <span>Add Recipe</span>
        </button>
      </div>

      {/* Filters */}
      <div className="virsa-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none z-10" />
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="virsa-input !pl-11"
              data-testid="recipe-search"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full md:w-48 h-12" data-testid="filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Recipes List by Category */}
      {sortedAndFilteredRecipes.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedRecipes).map(([category, categoryRecipes]) => (
            <div key={category}>
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-4 h-4 rounded-full ${getCategoryColor(category)}`} />
                <h2 className="text-lg font-bold text-stone-700">{category}</h2>
                <span className="text-sm text-stone-400">({categoryRecipes.length})</span>
              </div>

              {/* Recipe Cards */}
              <div className="space-y-2">
                {categoryRecipes.map(recipe => (
                  <div
                    key={recipe.id}
                    onClick={() => navigate(`/recipes/${recipe.id}/view`)}
                    className={`virsa-card border-l-4 ${getCategoryBgColor(recipe.category)} p-4 cursor-pointer transition-all`}
                    data-testid={`recipe-card-${recipe.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <ChefHat size={20} className="text-stone-400" />
                        <div>
                          <h3 className="font-bold text-stone-800">{recipe.recipe_name}</h3>
                          <p className="text-sm text-stone-500">
                            {recipe.ingredients.length} ingredients
                            {!recipe.active && <span className="text-red-500 ml-2">(Inactive)</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="virsa-card p-12 text-center">
          <ChefHat size={48} className="mx-auto text-stone-300 mb-4" />
          <h3 className="text-xl font-semibold text-stone-600 mb-2">No recipes found</h3>
          <p className="text-stone-500 mb-4">
            {searchQuery || filterCategory !== 'all'
              ? 'Try adjusting your filters'
              : 'Start by adding your first recipe'}
          </p>
          <button onClick={() => navigate('/recipes/new')} className="btn-primary">
            <Plus size={20} />
            Add Recipe
          </button>
        </div>
      )}
    </div>
    </ScreenshotProtection>
  );
};
